import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";
import { useAuth } from "../../hooks/useAuth";
import { useBookingCart } from "../../hooks/useBookingCart";
import {
  listBookings,
  getBooking,
  previewStaffBooking,
  createStaffBooking,
  listStaffCoachClubs,
  searchBookingPayment,
  confirmBookingManualPayment,
  getBookingShroffVoucherPdf,
} from "../../services/bookingService";
import { listMembers } from "../../services/memberService";
import { listFacilities, getStaffFacilitySchedule } from "../../services/facilityService";
import {
  TOKENS,
  Icon,
  ICONS,
  Pill,
  EmptyState,
  Spinner,
  inputClass,
  labelClass,
  btnPrimary,
  btnSecondary,
  Pagination,
  usePagination,
  DEFAULT_PAGE_SIZE,
} from "../../components/common/DashboardUI";
import ReceiptFileInput from "../../components/common/ReceiptFileInput";
import { FEATURE_FLAGS } from "../../config/featureFlags";

// ── Modal shell ─────────────────────────────────────────────────────────────
function Modal({ title, onClose, children }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
        >
          <Icon path={ICONS.x} className="w-5 h-5" width={2} />
        </button>
        <h2 className="text-lg font-bold mb-4 pr-8" style={{ color: TOKENS.NAVY }}>
          {title}
        </h2>
        {children}
      </div>
    </div>
  );
}

const memberName = (b) => [b?.title, b?.initials, b?.lastname].filter(Boolean).join(" ") || "—";
const memberFullName = (m) => [m?.title, m?.name_denoted_by_initials, m?.lastname].filter(Boolean).join(" ") || "—";

function errInfo(err, fallback) {
  const data = err?.response?.data;
  return { message: data?.message || fallback, fields: data?.errors || {} };
}

const money = (v) => (v == null || v === "" ? "—" : `LKR ${Number(v).toLocaleString("en-LK", { minimumFractionDigits: 2 })}`);

const fmtDateTime = (v) => {
  if (!v) return "—";
  const s = String(v).replace("T", " ");
  return s.length >= 16 ? s.slice(0, 16) : s;
};

// Opens a blob in a pre-opened tab (must be window.open'd synchronously
// inside the triggering click to avoid the popup blocker).
function openBlobInTab(blob, tab) {
  const url = URL.createObjectURL(blob);
  if (tab && !tab.closed) tab.location.href = url;
  else window.open(url, "_blank");
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

// ── Booking detail (read-only) ──────────────────────────────────────────────
function ViewBookingModal({ bookingId, onClose }) {
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let on = true;
    getBooking(bookingId)
      .then((b) => on && setBooking(b))
      .catch((err) => on && setError(err?.response?.data?.message || "Could not load booking details."))
      .finally(() => on && setLoading(false));
    return () => {
      on = false;
    };
  }, [bookingId]);

  return (
    <Modal title="Booking Details" onClose={onClose}>
      {loading ? (
        <div className="flex flex-col items-center justify-center py-14 gap-3">
          <Spinner />
          <p className="text-sm text-gray-400">Loading…</p>
        </div>
      ) : error ? (
        <p className="text-sm text-red-600">{error}</p>
      ) : (
        <div className="space-y-5">
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
            {[
              ["Member", memberName(booking)],
              ["Member code", booking.member_code],
              ["Total entries", booking.total_entries],
              ["Total slots", booking.total_slots],
              ["Total fee", booking.total_fee != null ? `LKR ${Number(booking.total_fee).toFixed(2)}` : null],
              ["Payment reference", booking.payment_reference],
              ["Payment status", booking.payment_status],
              ["Paid amount", booking.paid_amount != null ? `LKR ${Number(booking.paid_amount).toFixed(2)}` : null],
            ]
              .filter(([, v]) => v != null && v !== "")
              .map(([label, value]) => (
                <div key={label} className="min-w-0">
                  <dt className="text-xs text-gray-400">{label}</dt>
                  <dd className="text-base font-medium text-gray-700 truncate" style={{ color: TOKENS.NAVY }}>
                    {value}
                  </dd>
                </div>
              ))}
          </dl>

          {booking.entries?.length > 0 && (
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-2">
                Entries {booking.entries.length > 1 ? `(${booking.entries.length})` : ""}
              </h4>
              <div className="space-y-1.5">
                {booking.entries.map((e) => (
                  <div key={e.id} className="flex items-center justify-between text-sm py-1.5 border-b border-gray-50 last:border-0">
                    <span className="text-gray-700">
                      {e.slot_code} — {e.booking_date} ({String(e.start_time).slice(0, 5)}–{String(e.end_time).slice(0, 5)})
                    </span>
                    <span className="font-semibold" style={{ color: TOKENS.PRIMARY }}>LKR {e.entry_fee}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end pt-1">
            <button type="button" onClick={onClose} className={btnSecondary}>
              Close
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ── Bookings tab (view-only ledger — unchanged from the original page) ─────
// Booking creation for a member's own account is a member-only flow (POST
// /api/bookings requires a MemberRegistration token), and there is no
// admin-facing approve/reject/cancel/complete endpoint despite those
// permissions being seeded — so this tab stays view-only. Staff-initiated
// booking now lives on the "Book for Member" tab instead.
function BookingsTab() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState("");
  const [viewingId, setViewingId] = useState(null);

  const fetchBookings = useCallback(() => {
    setLoading(true);
    const params = {};
    if (date) params.date = date;
    listBookings(params)
      .then(setBookings)
      .catch((err) => toast.error(err?.response?.data?.message || "Could not load bookings."))
      .finally(() => setLoading(false));
  }, [date]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const { page, setPage, totalPages, pageItems: pagedBookings } = usePagination(bookings, {
    pageSize: DEFAULT_PAGE_SIZE,
    resetKey: date,
  });

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input type="date" className={`${inputClass} w-auto`} value={date} onChange={(e) => setDate(e.target.value)} />
        {date && (
          <button onClick={() => setDate("")} className="text-xs font-semibold text-gray-500 hover:text-gray-800">
            Clear date
          </button>
        )}
      </div>

      <div className="bg-white rounded-2xl shadow-sm p-5">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <Spinner />
            <p className="text-sm text-gray-400">Loading bookings…</p>
          </div>
        ) : bookings.length === 0 ? (
          <EmptyState icon={ICONS.bookings} text="No bookings found." />
        ) : (
          <div className="overflow-x-auto -mx-5 px-5">
            <table className="w-full text-left min-w-[760px]">
              <thead>
                <tr className="text-xs text-gray-400 border-b border-gray-100">
                  <th className="py-2 pr-3 font-medium">Member</th>
                  <th className="py-2 px-3 font-medium">Date</th>
                  <th className="py-2 px-3 font-medium">Slots</th>
                  <th className="py-2 px-3 font-medium">Total Fee</th>
                  <th className="py-2 px-3 font-medium">Payment</th>
                  <th className="py-2 pl-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagedBookings.map((b) => (
                  <tr key={b.id} className="border-b border-gray-50 last:border-0">
                    <td className="py-3 pr-3">
                      <p className="text-sm font-semibold" style={{ color: TOKENS.NAVY }}>
                        {memberName(b)}
                      </p>
                      <p className="text-xs text-gray-400 truncate">{b.email}</p>
                    </td>
                    <td className="py-3 px-3 text-sm text-gray-600 whitespace-nowrap">{b.booking_date}</td>
                    <td className="py-3 px-3 text-sm text-gray-600">{b.total_slots}</td>
                    <td className="py-3 px-3 text-sm text-gray-600">
                      {b.total_fee != null ? `LKR ${Number(b.total_fee).toFixed(2)}` : "—"}
                    </td>
                    <td className="py-3 px-3">
                      <Pill>{b.payment_status}</Pill>
                    </td>
                    <td className="py-3 pl-3">
                      <div className="flex items-center justify-end gap-3">
                        <button
                          onClick={() => setViewingId(b.id)}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-gray-800"
                        >
                          <Icon path={ICONS.eye} className="w-4 h-4" width={1.8} />
                          View
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && bookings.length > 0 && (
          <div className="flex items-center justify-between pt-4 mt-1 border-t border-gray-100">
            <p className="text-xs text-gray-400">
              Page {page} of {totalPages} · {bookings.length.toLocaleString()} bookings
            </p>
            <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
          </div>
        )}
      </div>

      {viewingId != null && <ViewBookingModal bookingId={viewingId} onClose={() => setViewingId(null)} />}
    </div>
  );
}

// Slot codes come back from the backend as e.g. "BASKETBALL-COURT-01" —
// reformat to a friendlier "Basketball Court – 01" for display. Mirrors
// MemberDashboard.jsx's own copy (not shared — that file doesn't export it).
// Swimming pool slots are an exception: "SWIMMING-POOL-01" becomes "Lane 01"
// since 01-04 are lanes in one pool, not four separate pools.
const prettifySlotCode = (code) => {
  const str = String(code || "");
  const parts = str.split("-");
  const num = parts[parts.length - 1];
  if (parts.length < 2 || !/^\d+$/.test(num)) return str;
  const name = parts
    .slice(0, -1)
    .join(" ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
  if (name === "Swimming Pool") return `Lane ${num}`;
  return `${name} – ${num}`;
};

// The backend only validates that booking_date is today-or-later
// (BookingController::validateBookingRequest — after_or_equal:today), not
// that the time itself hasn't already gone by today, so a row whose start
// time has passed is only ever screened out here, client-side.
const isPastSlot = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) return false;
  const slotStart = new Date(`${dateStr}T${String(timeStr).slice(0, 5)}:00`);
  return !Number.isNaN(slotStart.getTime()) && slotStart.getTime() <= Date.now();
};

// A row is selectable if it's neither blocked, full, nor already past for
// the given booking date. Guards against undefined so it's safe to call on
// out-of-bounds neighbors (row before the first / after the last time slot).
const rowSelectable = (row, dateStr) =>
  Boolean(row) && Boolean(row.available) && !row.is_blocked && !isPastSlot(dateStr, row.start_time);

// Swatch color for one 30-minute grid cell's status key — "full" (no
// capacity left) reads as "Booked" in the UI since that's what actually
// produces it in practice; "min60" and "past" both collapse into the same
// muted "Full" swatch (their tooltip still explains the real reason).
const SWATCH_COLORS = {
  available: "#22c55e",
  blocked: TOKENS.NAVY,
  full: "#ef4444",
  min60: "#d1d5db",
  past: "#d1d5db",
};

// Status + tooltip for one 30-minute grid cell. `hasAvailableNeighbor` means
// at least one immediately-adjacent row (previous or next) is itself
// selectable — a cell with neither can never grow into a valid >=1-hour
// booking since selection can only ever extend to an adjacent cell.
const cellStatus = (ts, hasAvailableNeighbor, isPast) => {
  if (ts.is_blocked) return { key: "blocked", tooltip: `Blocked${ts.block_reason ? `: ${ts.block_reason}` : ""}` };
  if (isPast) return { key: "past", tooltip: "This time slot has already passed" };
  if (!ts.available) return { key: "full", tooltip: "Already booked" };
  if (!hasAvailableNeighbor) return { key: "min60", tooltip: "Not enough consecutive availability for a 1-hour booking" };
  return { key: "available", tooltip: `Available · from ${money(ts.fee)}` };
};

const SLOT_POPOVER_WIDTH = 256; // px — matches the w-64 the popover renders at

// Fixed-position coordinates for a slot popover anchored below `rect`
// (a trigger element's getBoundingClientRect()), clamped so it never runs
// off the right/left edge of the viewport.
const slotPopoverPosition = (rect) => {
  const left = Math.min(
    Math.max(rect.left + rect.width / 2 - SLOT_POPOVER_WIDTH / 2, 8),
    window.innerWidth - SLOT_POPOVER_WIDTH - 8
  );
  return { top: rect.bottom + 8, left };
};

// Rich popover shown on hover/click for a cell that has existing bookings
// (from the staff schedule endpoint's `bookings` array, absent on the
// member-facing /availability route) — who holds it, what time range, which
// booking. There's no formatted booking reference on this endpoint (only the
// raw numeric `booking_id`), so the ID is shown as-is rather than invented.
//
// Rendered via a portal into document.body rather than as an absolutely
// positioned child of the cell: the slot grid's wrapper scrolls
// (overflow-x-auto/overflow-y-auto for the "26rem"-tall table), and CSS
// clips any absolutely positioned descendant at that ancestor's scroll
// bounds regardless of z-index — an in-place popover was getting cut off
// after just its first line. Portaling out to the body + `position: fixed`
// coordinates from the trigger's own getBoundingClientRect() sidesteps that
// clipping entirely.
function SlotPopover({ ts, isFull, rect, onMouseEnter, onMouseLeave }) {
  const { top, left } = slotPopoverPosition(rect);
  return createPortal(
    <div
      className="fixed z-50 bg-white rounded-xl shadow-xl border border-gray-100 p-3 text-left cursor-default"
      style={{ top, left, width: SLOT_POPOVER_WIDTH }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <p className="text-sm font-bold" style={{ color: isFull ? "#b91c1c" : TOKENS.NAVY }}>
        {isFull ? "Slot Unavailable" : "Slot Partially Booked"}
      </p>
      <p className="text-xs text-gray-500 mt-0.5 mb-2">
        {isFull ? "This slot is already booked." : "This slot has an existing booking, but capacity remains."}
      </p>
      <div className="space-y-2">
        {(ts.bookings || []).map((b) => (
          <div key={b.booking_id} className="text-xs text-gray-600 border-t border-gray-50 pt-2 first:border-0 first:pt-0 space-y-0.5">
            <p><span className="font-semibold text-gray-500">Booked by:</span> {b.member_name || b.member_code || "Unknown"}</p>
            <p><span className="font-semibold text-gray-500">Time:</span> {String(ts.start_time).slice(0, 5)} – {String(ts.end_time).slice(0, 5)}</p>
            <p><span className="font-semibold text-gray-500">Booking ID:</span> #{b.booking_id}</p>
          </div>
        ))}
      </div>
    </div>,
    document.body
  );
}

function LegendDot({ color, border, label }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="w-3.5 h-3.5 rounded border" style={{ backgroundColor: color, borderColor: border }} />
      {label}
    </span>
  );
}

// Given the current selection (or null) and a clicked cell, resolves what
// should happen — the whole multi-slot-selection rule set lives here:
//   - No selection yet, or clicking a different court: start a fresh
//     single-cell selection there (a genuine change of context, not a gap).
//   - Clicking the cell immediately before/after the current range: extend
//     that end by one slot.
//   - Clicking the range's own start/end cell: shrink from that end (or
//     clear entirely if the range is down to one cell).
//   - Clicking a cell already inside the range (not an edge): no-op — can't
//     remove from the middle without breaking contiguity.
//   - Clicking anywhere else in the SAME court, non-adjacent to the current
//     range: blocked (`blocked: true`) rather than silently jumping the
//     selection there — that click would otherwise look like it's bridging
//     over the unselected/unavailable slots in between. The caller surfaces
//     why and leaves the existing selection untouched; the user can shrink
//     it back to nothing (or switch courts) to start over elsewhere.
function resolveSelectionClick(prev, slot, idx) {
  if (!prev || prev.facilitySlotId !== slot.id) {
    return { next: { facilitySlotId: slot.id, slotCode: slot.slot_code, startIdx: idx, endIdx: idx } };
  }
  if (idx === prev.startIdx - 1) return { next: { ...prev, startIdx: idx } };
  if (idx === prev.endIdx + 1) return { next: { ...prev, endIdx: idx } };
  if (idx === prev.startIdx) return { next: prev.startIdx === prev.endIdx ? null : { ...prev, startIdx: prev.startIdx + 1 } };
  if (idx === prev.endIdx) return { next: prev.startIdx === prev.endIdx ? null : { ...prev, endIdx: prev.endIdx - 1 } };
  if (idx > prev.startIdx && idx < prev.endIdx) return { next: prev };
  return { next: prev, blocked: true };
}

const formatDuration = (mins) => {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
};

const todayStr = () => new Date().toISOString().slice(0, 10);

// Client-side hint only for the staff booking-date cap (MultiEntryBookingService::
// STAFF_MAX_BOOKING_DAYS = 90) — the backend is authoritative regardless.
const maxDateStr = (daysAhead) => {
  const d = new Date();
  d.setDate(d.getDate() + daysAhead);
  return d.toISOString().slice(0, 10);
};

// ── Book for Member tab (staff booking — shroff/manual-confirm only) ───────
// POST /api/staff/bookings takes the same multi-entry { member_id,
// facility_id, entries: [...] } shape as member self-booking (up to 10
// dated time-slot entries, all one facility) — this replaced the old
// separate date-range "bulk" endpoint entirely; multi-date staff booking is
// now just adding several dated entries to the same cart. Locks every
// entry's slots until midnight tonight, since the member isn't present to
// pay online themselves. BookingController::manualConfirm explicitly
// rejects a member self-booking (created_by_user_id empty) — staff bookings
// are shroff-counter-pay only, so there is no gateway popup here, just an
// inline "Confirm Payment" step straight after creation (same shape as the
// Confirm Payment tabs on MembershipRenewals.jsx / ClubRenewals.jsx /
// IdCardReprints.jsx).
function StaffBookingTab() {
  const { can } = useAuth();
  // Step 1 — member picker. A single search bar for name/email/member code.
  // If the role has "view_members", typing >=2 chars debounces a real
  // GET /api/members?search= lookup and shows a dropdown to pick from (same
  // as before). If it doesn't (or nothing was picked from the dropdown),
  // the raw text is sent straight through as member_code/email at submit
  // time and resolved server-side — so a role with create_bookings but not
  // view_members can still use this tab, just without the live dropdown.
  const canSearchMembers = can("view_members");
  const [memberSearch, setMemberSearch] = useState("");
  const [memberResults, setMemberResults] = useState([]);
  const [memberSearchLoading, setMemberSearchLoading] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  // Clubs the entered member coaches for (GET /api/staff/coach-clubs) —
  // empty for a non-coach. A coach booking must name one of them as
  // club_id on both preview and create, so it's picked here alongside the
  // member; a single club is preselected.
  const [coachClubs, setCoachClubs] = useState([]);
  const [coachClubsLoading, setCoachClubsLoading] = useState(false);
  const [clubId, setClubId] = useState("");

  // Step 2 — facility / date / slot picker
  const [facilities, setFacilities] = useState([]);
  const [facilityId, setFacilityId] = useState("");
  const [date, setDate] = useState(todayStr());
  const [availability, setAvailability] = useState(null);
  const [availLoading, setAvailLoading] = useState(false);
  // { facilitySlotId, slotCode, startIdx, endIdx } — a contiguous run of
  // 30-min rows (both indices inclusive) within one court's time_slots.
  // null when nothing selected. See resolveSelectionClick() above for the click rules.
  const [selection, setSelection] = useState(null);
  // Slot popover (bookings detail) — each is { key, rect }, rect being the
  // trigger's getBoundingClientRect() at the moment of interaction (used to
  // position the portaled popover — see SlotPopover). `hovered` shows it
  // transiently on mouseover; `pinned` keeps it open after a click on a
  // full/unavailable cell, until clicked again or the click-away layer
  // fires. Pinned wins when both are set for different cells.
  const [hovered, setHovered] = useState(null);
  const [pinned, setPinned] = useState(null);
  const activePopover = pinned || hovered;

  // Multi-entry booking cart — up to 10 dated time-slot entries, all on one
  // facility, built up via "Add to Booking" before a single Create Booking.
  // This is what replaced the old date-range "bulk" feature: staff can
  // change `date` between adds instead of picking one contiguous range, and
  // dates can be up to 90 days out (STAFF_MAX_BOOKING_DAYS) vs a member's 30.
  // { member_id } if a search result was picked, otherwise { member_code }
  // or { email } (auto-detected by "@") built straight from whatever's
  // still typed in the search bar — resolveMemberIdFromInput() on the
  // backend accepts any one of the three. null when nothing's usable yet.
  const buildMemberPayload = () => {
    if (selectedMember) return { member_id: selectedMember.id };
    const value = memberSearch.trim();
    if (!value) return null;
    return value.includes("@") ? { email: value } : { member_code: value };
  };

  const memberPayload = buildMemberPayload();
  const memberPayloadKey = JSON.stringify(memberPayload);
  const needsClub = coachClubs.length > 0;

  // Member payload plus club_id for a coach. null while the coach's clubs
  // are still loading or no club is picked yet, since both staff routes
  // 422 a coach booking without one.
  const buildBookingPayload = () => {
    if (!memberPayload || coachClubsLoading) return null;
    if (!needsClub) return memberPayload;
    return clubId ? { ...memberPayload, club_id: Number(clubId) } : null;
  };

  // The staff preview route requires a member too (it 422s "Provide
  // member_id, member_code, or email for staff bookings." without one), so
  // the member payload rides along on every preview, and a null payload
  // holds the preview off until a member (and, for a coach, a club) is entered.
  const cart = useBookingCart({
    audience: "staff",
    previewFn: previewStaffBooking,
    previewExtra: buildBookingPayload(),
  });

  // Re-fetch the coach's clubs whenever the entered member changes —
  // debounced, since a typed member_code/email changes on every keystroke.
  // A lookup that fails (e.g. a half-typed code that doesn't resolve yet)
  // just means "no clubs"; the create call reports the real error.
  useEffect(() => {
    const payload = JSON.parse(memberPayloadKey);
    setCoachClubs([]);
    setClubId("");
    if (!payload) {
      setCoachClubsLoading(false);
      return undefined;
    }
    let on = true;
    setCoachClubsLoading(true);
    const t = setTimeout(() => {
      listStaffCoachClubs(payload)
        .then((data) => {
          if (!on) return;
          const clubs = Array.isArray(data?.clubs) ? data.clubs : [];
          setCoachClubs(clubs);
          if (clubs.length === 1) setClubId(String(clubs[0].id));
        })
        .catch(() => on && setCoachClubs([]))
        .finally(() => on && setCoachClubsLoading(false));
    }, payload.member_id ? 0 : 400);
    return () => {
      on = false;
      clearTimeout(t);
    };
  }, [memberPayloadKey]);

  // Step 3 — create booking / confirm payment
  const [creating, setCreating] = useState(false);
  const [createdBooking, setCreatedBooking] = useState(null); // createStaffBooking() response
  const [paidAmount, setPaidAmount] = useState("");
  const [paidDate, setPaidDate] = useState(todayStr());
  const [receiptReference, setReceiptReference] = useState("");
  const [receiptFile, setReceiptFile] = useState(null);
  const [note, setNote] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState("");
  const [confirmFieldErrors, setConfirmFieldErrors] = useState({});
  const [confirmResult, setConfirmResult] = useState(null);
  const [downloadingVoucher, setDownloadingVoucher] = useState(false);

  const confirmFieldError = (key) => {
    const msgs = confirmFieldErrors[key];
    return Array.isArray(msgs) && msgs[0] ? msgs[0] : null;
  };

  useEffect(() => {
    listFacilities()
      .then((list) => {
        setFacilities(list);
        if (list.length) setFacilityId((id) => id || String(list[0].id));
      })
      .catch(() => toast.error("Could not load facilities."));
  }, []);

  // Debounced member search — only if this role can browse the member list
  // at all, and only once nothing's been picked yet. Skipped entirely for a
  // role without view_members, so the search bar still works for them (the
  // raw text is just sent straight through as member_code/email instead).
  useEffect(() => {
    if (!canSearchMembers || selectedMember || !memberSearch.trim() || memberSearch.trim().length < 2) {
      setMemberResults([]);
      return undefined;
    }
    let on = true;
    setMemberSearchLoading(true);
    const t = setTimeout(() => {
      listMembers({ search: memberSearch.trim() })
        .then((list) => on && setMemberResults(list))
        .catch(() => on && setMemberResults([]))
        .finally(() => on && setMemberSearchLoading(false));
    }, 300);
    return () => {
      on = false;
      clearTimeout(t);
    };
  }, [canSearchMembers, memberSearch, selectedMember]);

  // Load the selected facility's availability for the chosen date. Uses the
  // staff schedule endpoint (not the plain member-facing /availability route)
  // so each cell also carries a `bookings` array — lets staff see who
  // currently holds a slot, not just available/unavailable.
  useEffect(() => {
    if (!facilityId || !date) return undefined;
    let on = true;
    setAvailLoading(true);
    setSelection(null);
    getStaffFacilitySchedule(facilityId, date)
      .then((data) => on && setAvailability(data))
      .catch(() => {
        if (on) {
          setAvailability(null);
          toast.error("Could not load availability for that facility/date.");
        }
      })
      .finally(() => on && setAvailLoading(false));
    return () => {
      on = false;
    };
  }, [facilityId, date]);

  // 30-minute row headers for the grid — every facility slot (court) shares
  // the same time_slots length/boundaries for a given date, so the first
  // slot's rows stand in for all of them.
  const timeCols = availability?.slots?.[0]?.time_slots || [];

  // Derive the actual "H:i" start/end + duration for the current selection
  // from availability + the selected row indices, same pattern as the member
  // self-booking flow (MemberDashboard.jsx) — selection itself only stores
  // indices, so this always reflects the live grid rather than a stale copy.
  const selectedSlotRows = selection ? availability?.slots?.find((s) => s.id === selection.facilitySlotId)?.time_slots || [] : [];
  const selectedStartRow = selection ? selectedSlotRows[selection.startIdx] : null;
  const selectedEndRow = selection ? selectedSlotRows[selection.endIdx] : null;
  const selectedStartTime = selectedStartRow ? String(selectedStartRow.start_time).slice(0, 5) : null;
  const selectedEndTime = selectedEndRow ? String(selectedEndRow.end_time).slice(0, 5) : null;
  const selectionDurationMinutes = selection ? (selection.endIdx - selection.startIdx + 1) * 30 : 0;

  const handleCellClick = (slot, idx) => {
    const rows = slot.time_slots || [];
    if (!rowSelectable(rows[idx], date)) return;
    const { next, blocked } = resolveSelectionClick(selection, slot, idx);
    if (blocked) {
      toast.error("Slots must be consecutive. Extend from the edge of your current selection, or clear it first.");
      return;
    }
    setSelection(next);
  };

  const clearSelection = () => setSelection(null);

  // Pushes the current grid selection into the cart, then clears it so staff
  // can immediately pick another date/slot (possibly a different date —
  // that's what replaces the old date-range "bulk" mode) without leaving
  // the page.
  const handleAddToCart = () => {
    if (!selection || selectionDurationMinutes < 60 || !selectedStartTime || !selectedEndTime) return;
    const result = cart.addEntry({
      facility_id: Number(facilityId),
      facility_slot_id: selection.facilitySlotId,
      slot_code: selection.slotCode,
      booking_date: date,
      start_time: selectedStartTime,
      end_time: selectedEndTime,
    });
    if (!result.ok) {
      toast.error(
        result.reason === "max_entries"
          ? "You can add up to 10 time slots per booking."
          : "All time slots in one booking must be at the same facility."
      );
      return;
    }
    setSelection(null);
  };

  const resetForNextBooking = () => {
    setSelectedMember(null);
    setMemberSearch("");
    setMemberResults([]);
    setSelection(null);
    cart.clearCart();
    setCreatedBooking(null);
    setPaidAmount("");
    setPaidDate(todayStr());
    setReceiptReference("");
    setReceiptFile(null);
    setNote("");
    setConfirmResult(null);
    setConfirmError("");
    setConfirmFieldErrors({});
  };

  const hasMember = !!selectedMember || memberSearch.trim().length > 0;

  // Confirms the whole cart in one checkout. Snapshots cart.entries so a
  // 409's positional entry_index can be mapped back to the right cart row.
  const handleCreateBooking = async () => {
    const bookingPayload = buildBookingPayload();
    if (creating || !bookingPayload || cart.entries.length === 0) return;
    const submittedEntries = cart.entries;
    setCreating(true);
    try {
      const r = await createStaffBooking({
        ...bookingPayload,
        facility_id: cart.facilityId,
        entries: submittedEntries.map(({ facility_slot_id, booking_date, start_time, end_time }) => ({
          facility_slot_id,
          booking_date,
          start_time,
          end_time,
        })),
      });
      toast.success(r.message || "Staff booking created.");
      setCreatedBooking(r);
      // Left blank on purpose — the operator types the amount actually received.
      setPaidAmount("");
    } catch (err) {
      const data = err?.response?.data;
      if (data?.error_code === "booking_entries_unavailable" || data?.error_code === "booking_entry_blocked") {
        cart.applyConflicts(data.conflicts, submittedEntries);
        toast.error(
          data.conflicts?.length === 1
            ? data.conflicts[0].user_message || data.message
            : `${data.conflicts?.length ?? "Some"} time slots in this booking are no longer available — see below.`
        );
      } else if (data?.error_code === "mixed_facilities") {
        toast.error("This booking mixed two different facilities unexpectedly — please start again.");
        cart.clearCart();
      } else {
        toast.error(errInfo(err, "Could not create this booking.").message);
      }
    } finally {
      setCreating(false);
    }
  };

  const handleDownloadVoucher = async () => {
    if (!createdBooking || downloadingVoucher) return;
    const paymentId = createdBooking.booking?.payment_id;
    if (!paymentId) return;
    const tab = window.open("", "_blank");
    setDownloadingVoucher(true);
    try {
      const blob = await getBookingShroffVoucherPdf(paymentId, createdBooking.payment_reference);
      openBlobInTab(blob, tab);
    } catch (err) {
      tab?.close?.();
      toast.error(errInfo(err, "Could not load the shroff voucher.").message);
    } finally {
      setDownloadingVoucher(false);
    }
  };

  const handleConfirmPayment = async (e) => {
    e.preventDefault();
    if (confirming || !createdBooking || !receiptFile) return;
    setConfirming(true);
    setConfirmError("");
    setConfirmFieldErrors({});
    try {
      const r = await confirmBookingManualPayment({
        payment_reference: createdBooking.payment_reference,
        paid_amount: paidAmount,
        paid_date: paidDate,
        receipt_reference: receiptReference.trim(),
        receipt: receiptFile,
        note: note.trim() || undefined,
      });
      toast.success(r.message || "Payment confirmed. Receipt pending director acknowledgement.");
      setConfirmResult(r);
    } catch (err) {
      const info = errInfo(err, "Could not confirm this payment.");
      setConfirmError(info.message);
      setConfirmFieldErrors(info.fields);
    } finally {
      setConfirming(false);
    }
  };

  // ── Confirm Payment step (after a booking has been created) ──────────────
  if (createdBooking) {
    return (
      <div className="max-w-xl space-y-4">
        <div className="bg-white rounded-2xl shadow-sm p-6 space-y-4">
          <div>
            <h3 className="font-bold text-base mb-1" style={{ color: TOKENS.NAVY }}>
              Confirm Shroff Payment
            </h3>
            <p className="text-sm text-gray-500">
              {createdBooking.message || "Booking locked for this member — payment must be completed before it expires."}
            </p>
          </div>

          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <div>
              <dt className="text-xs text-gray-400">Member</dt>
              <dd className="font-medium" style={{ color: TOKENS.NAVY }}>
                {selectedMember
                  ? `${memberFullName(selectedMember)} (${selectedMember.member_code})`
                  : createdBooking.booking?.member_code || memberSearch}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-gray-400">Payment reference</dt>
              <dd className="font-mono font-medium" style={{ color: TOKENS.NAVY }}>
                {createdBooking.payment_reference}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-gray-400">Payable amount</dt>
              <dd className="font-semibold" style={{ color: TOKENS.NAVY }}>
                {money(createdBooking.payable_amount)}
              </dd>
            </div>
            {createdBooking.booking?.entries?.length > 1 && (
              <div>
                <dt className="text-xs text-gray-400">Time slots booked</dt>
                <dd className="font-semibold" style={{ color: TOKENS.NAVY }}>
                  {createdBooking.booking.entries.length}
                </dd>
              </div>
            )}
          </dl>

          {!confirmResult && createdBooking.booking?.payment_id && (
            <button
              type="button"
              onClick={handleDownloadVoucher}
              disabled={downloadingVoucher}
              className={`${btnSecondary} !w-auto px-4 inline-flex items-center gap-2`}
            >
              <Icon path={ICONS.download} className="w-4 h-4" width={1.8} />
              {downloadingVoucher ? "Opening…" : "Download Shroff Voucher"}
            </button>
          )}

          {confirmResult ? (
            <div className="rounded-lg bg-emerald-50 border border-emerald-100 px-4 py-3 text-sm text-emerald-800">
              {confirmResult.message || "Payment confirmed. Receipt pending director acknowledgement."}{" "}
              {confirmResult.approval_id && (
                <>
                  Approval ID: <span className="font-mono font-semibold">#{confirmResult.approval_id}</span> — will
                  be acknowledged on the Payments page's Manual Approvals tab.
                </>
              )}
              <div className="pt-3">
                <button type="button" onClick={resetForNextBooking} className={`${btnPrimary} !w-auto px-6`}>
                  Book Another
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleConfirmPayment} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>
                    Paid amount <span className="text-gray-400 font-normal">(must match payable amount)</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className={inputClass}
                    value={paidAmount}
                    onChange={(e) => setPaidAmount(e.target.value)}
                    required
                  />
                  {confirmFieldError("paid_amount") && <p className="text-xs text-red-600 mt-1">{confirmFieldError("paid_amount")}</p>}
                </div>
                <div>
                  <label className={labelClass}>Paid date</label>
                  <input type="date" className={inputClass} value={paidDate} onChange={(e) => setPaidDate(e.target.value)} required />
                  {confirmFieldError("paid_date") && <p className="text-xs text-red-600 mt-1">{confirmFieldError("paid_date")}</p>}
                </div>
              </div>

              <div>
                <label className={labelClass}>Receipt / slip reference</label>
                <input
                  className={inputClass}
                  value={receiptReference}
                  onChange={(e) => setReceiptReference(e.target.value)}
                  required
                />
                {confirmFieldError("receipt_reference") && (
                  <p className="text-xs text-red-600 mt-1">{confirmFieldError("receipt_reference")}</p>
                )}
              </div>
              <ReceiptFileInput file={receiptFile} onChange={setReceiptFile} error={confirmFieldError("receipt")} />

              <div>
                <label className={labelClass}>
                  Note <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <textarea className={`${inputClass} resize-none`} rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
              </div>

              {confirmError && <p className="text-sm text-red-600">{confirmError}</p>}

              <div className="flex gap-3">
                <button type="submit" disabled={confirming || !receiptFile} className={`${btnPrimary} !w-auto px-6`}>
                  {confirming ? "Confirming…" : "Confirm Payment"}
                </button>
                <button type="button" onClick={resetForNextBooking} className={`${btnSecondary} !w-auto px-6`}>
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    );
  }

  // ── Booking form step ──────────────────────────────────────────────────
  // Pending-selection summary + Add to Booking button, shared by two render
  // sites: inside the "Select Booking Details" card on desktop (unchanged
  // layout), and in a separate card below "Available Time Slots" on mobile
  // (`lg:hidden` there, `hidden lg:block` at the desktop site) — mobile-only
  // repositioning via CSS visibility, not a different DOM structure per
  // breakpoint. Fee is shown per-cart-entry (and as a running total) once
  // "Add to Booking" pushes this selection into the cart, not here — lighting
  // is fully server-computed now too, so there's nothing to preview or
  // toggle before adding.
  const selectedSlotSummary = (
    <div className="rounded-xl border border-blue-100 bg-blue-50 p-4 space-y-3">
      <p className="text-sm font-bold flex items-center gap-1.5" style={{ color: TOKENS.NAVY }}>
        <Icon path={ICONS.check} className="w-4 h-4" stroke={TOKENS.PRIMARY} width={2.4} />
        Selected Slot
      </p>
      <dl className="text-sm space-y-1.5">
        <div className="flex justify-between gap-2">
          <dt className="text-gray-500">Court</dt>
          <dd className="font-semibold text-right" style={{ color: TOKENS.NAVY }}>
            {prettifySlotCode(selection?.slotCode)}
          </dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-gray-500">Date</dt>
          <dd className="font-semibold" style={{ color: TOKENS.NAVY }}>{date}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-gray-500">Time</dt>
          <dd className="font-semibold" style={{ color: TOKENS.NAVY }}>
            {selectedStartTime}–{selectedEndTime}
          </dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-gray-500">Duration</dt>
          <dd className="font-semibold" style={{ color: TOKENS.NAVY }}>
            {formatDuration(selectionDurationMinutes)}
          </dd>
        </div>
      </dl>
      {cart.isFull && (
        <p className="text-xs font-semibold text-amber-600">Maximum 10 slots per booking — remove one below to add another.</p>
      )}
      <button type="button" onClick={clearSelection} className={`${btnSecondary} w-full !py-1.5`}>
        Clear Selection
      </button>
    </div>
  );

  const addToCartButton = (
    <button
      type="button"
      onClick={handleAddToCart}
      disabled={!selection || selectionDurationMinutes < 60 || cart.isFull}
      className={`${btnPrimary} w-full`}
    >
      Add to Booking
    </button>
  );

  // Full-width panel listing every cart entry — shown once at least one
  // slot has been added, below both the details card and the grid.
  const cartPanel = cart.entries.length === 0 ? null : (
    <div className="bg-white rounded-2xl shadow-sm p-6 space-y-4">
      <h3 className="font-bold text-base" style={{ color: TOKENS.NAVY }}>
        Booking ({cart.entries.length}/{cart.maxEntries})
      </h3>
      <div className="divide-y divide-gray-100 border border-gray-100 rounded-xl overflow-hidden">
        {cart.entries.map((entry, idx) => {
          const previewEntry = cart.preview?.entries?.[idx];
          const conflict = cart.entryConflicts.get(entry.clientKey);
          return (
            <div key={entry.clientKey} className={`p-3 flex items-center justify-between gap-3 ${conflict ? "bg-red-50" : ""}`}>
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate" style={{ color: TOKENS.NAVY }}>
                  {prettifySlotCode(entry.slot_code)} — {entry.booking_date}
                </p>
                <p className="text-xs text-gray-500">
                  {entry.start_time} - {entry.end_time}
                  {previewEntry && <span className="ml-2 font-semibold" style={{ color: TOKENS.PRIMARY }}>LKR {previewEntry.entry_fee}</span>}
                </p>
                {conflict && (
                  <p className="text-xs font-medium text-red-600 mt-1">
                    {conflict.user_message || conflict.reason_label || "This slot is no longer available."}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => cart.removeEntry(entry.clientKey)}
                className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:bg-gray-100 hover:text-red-500 transition"
                title="Remove this slot"
              >
                <Icon path={ICONS.x} className="w-4 h-4" width={2} />
              </button>
            </div>
          );
        })}
      </div>
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-500">
          {cart.previewLoading ? "Calculating…" : cart.preview ? `${cart.preview.total_slots} slots total` : "—"}
        </span>
        <span className="font-bold text-base" style={{ color: TOKENS.PRIMARY }}>
          {cart.previewLoading ? "…" : cart.preview ? `LKR ${cart.preview.total_fee}` : "—"}
        </span>
      </div>
      <button
        type="button"
        onClick={handleCreateBooking}
        disabled={
          creating ||
          !hasMember ||
          coachClubsLoading ||
          (needsClub && !clubId) ||
          cart.previewLoading ||
          cart.entryConflicts.size > 0
        }
        className={`${btnPrimary} w-full`}
      >
        {creating ? "Creating…" : "Create Booking"}
      </button>
      {!hasMember && <p className="text-xs text-amber-600 text-center">Enter or select a member above before creating this booking.</p>}
      {hasMember && needsClub && !clubId && (
        <p className="text-xs text-amber-600 text-center">Select the club this coach is booking for.</p>
      )}
    </div>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-start">
      {/* Left — booking details */}
      <div className="lg:col-span-1 bg-white rounded-2xl shadow-sm p-6 space-y-5">
        <h3 className="font-bold text-base" style={{ color: TOKENS.NAVY }}>
          Select Booking Details
        </h3>

        <div>
          <label className={labelClass}>Member</label>
          {selectedMember ? (
            <div className="flex items-center justify-between gap-2 rounded-lg border border-gray-200 px-3 py-2.5">
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate" style={{ color: TOKENS.NAVY }}>
                  {selectedMember.member_code} — {memberFullName(selectedMember)}
                </p>
                <p className="text-xs text-gray-400 truncate">{selectedMember.email}</p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedMember(null)}
                className="text-xs font-semibold flex-shrink-0"
                style={{ color: TOKENS.PRIMARY }}
              >
                Change
              </button>
            </div>
          ) : (
            <div className="relative">
              <input
                className={`${inputClass} pr-9`}
                placeholder="Name, email, or member code (e.g. MEM000123)…"
                value={memberSearch}
                onChange={(e) => setMemberSearch(e.target.value)}
              />
              <Icon
                path={ICONS.search}
                className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
                stroke="#9ca3af"
                width={2}
              />
              {/* Live dropdown only for a role that can browse the member
                  list — otherwise the typed text is used as-is (member_code
                  or email, auto-detected) when the booking is created, no
                  dropdown needed. */}
              {canSearchMembers && memberSearch.trim().length >= 2 && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                  {memberSearchLoading ? (
                    <p className="text-xs text-gray-400 px-3 py-2">Searching…</p>
                  ) : memberResults.length === 0 ? (
                    <p className="text-xs text-gray-400 px-3 py-2">
                      No members found — you can still continue with this exact code or email.
                    </p>
                  ) : (
                    memberResults.map((m) => (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => {
                          setSelectedMember(m);
                          setMemberResults([]);
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-50 last:border-0"
                      >
                        <p className="text-sm font-semibold" style={{ color: TOKENS.NAVY }}>
                          {memberFullName(m)}
                        </p>
                        <p className="text-xs text-gray-400">
                          {m.member_code} — {m.email}
                        </p>
                      </button>
                    ))
                  )}
                </div>
              )}
              {!canSearchMembers && memberSearch.trim().length > 0 && (
                <p className="text-xs text-gray-400 mt-1">
                  Will be looked up by exact member code or email when you create the booking.
                </p>
              )}
            </div>
          )}
        </div>

        {(coachClubsLoading || needsClub) && (
          <div>
            <label className={labelClass}>Club</label>
            {coachClubsLoading ? (
              <p className="text-xs text-gray-400">Checking this member's clubs…</p>
            ) : (
              <>
                <select className={inputClass} value={clubId} onChange={(e) => setClubId(e.target.value)}>
                  {coachClubs.length > 1 && <option value="">Select a club…</option>}
                  {coachClubs.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.club_name}
                      {c.club_code ? ` (${c.club_code})` : ""}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-400 mt-1">This member is a coach — choose the club this booking is for.</p>
              </>
            )}
          </div>
        )}

        <div>
          <label className={labelClass}>Facility</label>
          <select className={inputClass} value={facilityId} onChange={(e) => setFacilityId(e.target.value)}>
            {facilities.map((f) => (
              <option key={f.id} value={f.id}>
                {f.facility_name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className={labelClass}>Date</label>
          <input
            type="date"
            className={inputClass}
            min={todayStr()}
            max={maxDateStr(90)}
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
          <p className="text-xs text-gray-400 mt-1">
            Add slots on different dates to the booking below — up to 10 in total, 90 days out.
          </p>
        </div>

        {selection ? (
          <div className="hidden lg:block">{selectedSlotSummary}</div>
        ) : (
          <div className="rounded-xl border border-blue-100 bg-blue-50 p-4 flex gap-3">
            <div
              className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: TOKENS.PRIMARY }}
            >
              <Icon path={ICONS.alertCircle} className="w-3.5 h-3.5" stroke="#fff" width={2.4} />
            </div>
            <div>
              <p className="text-sm font-bold" style={{ color: TOKENS.NAVY }}>
                Note
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                Select a time slot from the available options on the right to add it to the booking.
              </p>
              <p className="text-xs text-gray-400 mt-1.5">
                Locks every slot until midnight tonight. The member pays at the counter — confirm the receipt on the
                next step.
              </p>
            </div>
          </div>
        )}

        <div className="hidden lg:block">{addToCartButton}</div>
      </div>

      {/* Right — available time slots */}
      <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm p-6 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="font-bold text-base" style={{ color: TOKENS.NAVY }}>
            Available Time Slots
          </h3>
          <div className="flex flex-wrap gap-4 text-xs text-gray-500">
            <LegendDot color="#22c55e" border="#22c55e" label="Available" />
            <LegendDot color="#f97316" border="#f97316" label="Selected" />
            <LegendDot color="#3b82f6" border="#3b82f6" label="In Cart" />
            <LegendDot color="#ef4444" border="#ef4444" label="Booked" />
            <LegendDot color={TOKENS.NAVY} border={TOKENS.NAVY} label="Blocked" />
            <LegendDot color="#d1d5db" border="#d1d5db" label="Full" />
          </div>
        </div>

        {availLoading ? (
          <div className="flex items-center gap-2 py-4">
            <Spinner />
            <p className="text-sm text-gray-400">Loading availability…</p>
          </div>
        ) : timeCols.length === 0 ? (
          <EmptyState icon={ICONS.bookings} text="No slots are available for this facility on this date." />
        ) : (
          <div className="max-h-[28rem] overflow-auto pb-1">
            <div className="space-y-2 w-max">
              {availability.slots.map((slot) => (
                <div key={slot.id} className="flex items-center gap-2">
                  {/* Sticky column's "left-0" anchors to this scroll
                      container's own edge — the old "-mx-1 px-1" pulled that
                      edge 0.25rem outside the sticky label's bg-white cover,
                      leaving a sliver of a scrolled-under slot button visible
                      to its left. Dropping it keeps the clip boundary and the
                      sticky anchor at the same x, so the label fully masks
                      whatever's scrolled behind it. */}
                  <p className="sticky left-0 z-10 bg-white w-28 flex-shrink-0 text-xs font-bold text-gray-500 pr-2">
                    {prettifySlotCode(slot.slot_code)}
                  </p>
                  <div className="flex gap-2">
                    {timeCols.map((tcol, idx) => {
                      const rows = slot.time_slots;
                      const ts = rows[idx];
                      const hasAvailableNeighbor = rowSelectable(rows[idx - 1], date) || rowSelectable(rows[idx + 1], date);
                      const isPast = isPastSlot(date, ts.start_time);
                      const status = cellStatus(ts, hasAvailableNeighbor, isPast);
                      const isSelected =
                        selection && selection.facilitySlotId === slot.id && idx >= selection.startIdx && idx <= selection.endIdx;
                      const hasBookings = (ts.bookings || []).length > 0;
                      const cellKey = `${slot.id}_${idx}`;
                      const isPinned = pinned?.key === cellKey;
                      const popoverOpen = hasBookings && activePopover?.key === cellKey;
                      const clearHover = () => setHovered((h) => (h?.key === cellKey ? null : h));
                      // Cart entries can span multiple 30-min cells (one
                      // "Add to Booking" click adds a single entry covering
                      // the whole selected range), so a cell counts as "in
                      // cart" if any entry's [start_time, end_time) range
                      // contains it — not just an exact start_time match.
                      const cellStart = String(ts.start_time).slice(0, 5);
                      const isInCart = cart.entries.some(
                        (e) =>
                          e.facility_slot_id === slot.id &&
                          e.booking_date === date &&
                          e.start_time <= cellStart &&
                          e.end_time > cellStart
                      );
                      const cellColor = isSelected ? "#f97316" : isInCart ? "#3b82f6" : SWATCH_COLORS[status.key];
                      const cellTextColor = status.key === "min60" || status.key === "past" ? "#4b5563" : "#ffffff";
                      return (
                        <div
                          key={cellKey}
                          className="relative w-28 flex-shrink-0"
                          onMouseEnter={(e) => hasBookings && setHovered({ key: cellKey, rect: e.currentTarget.getBoundingClientRect() })}
                          onMouseLeave={clearHover}
                        >
                          <button
                            type="button"
                            disabled={(!rowSelectable(ts, date) && !hasBookings) || isInCart}
                            title={
                              isInCart
                                ? "Already added to this booking below — remove it there to change it"
                                : hasBookings
                                ? undefined
                                : isSelected
                                ? "Selected — click the first or last selected slot to remove it"
                                : status.tooltip
                            }
                            onClick={(e) => {
                              if (hasBookings && !rowSelectable(ts, date)) {
                                setPinned((p) =>
                                  p?.key === cellKey ? null : { key: cellKey, rect: e.currentTarget.getBoundingClientRect() }
                                );
                                return;
                              }
                              handleCellClick(slot, idx);
                            }}
                            className="w-full h-9 flex items-center justify-center rounded-lg px-1 text-xs font-semibold whitespace-nowrap transition hover:brightness-95 disabled:cursor-not-allowed"
                            style={{ backgroundColor: cellColor, color: cellTextColor }}
                          >
                            {String(tcol.start_time).slice(0, 5)}–{String(tcol.end_time || "").slice(0, 5)}
                          </button>

                          {popoverOpen && (
                            <>
                              {isPinned && <div className="fixed inset-0 z-40" onClick={() => setPinned(null)} />}
                              <SlotPopover
                                ts={ts}
                                isFull={status.key === "full"}
                                rect={activePopover.rect}
                                onMouseEnter={() => setHovered({ key: cellKey, rect: activePopover.rect })}
                                onMouseLeave={clearHover}
                              />
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <p className="flex items-center gap-2 text-xs text-gray-400 pt-3 border-t border-gray-100">
          <Icon path={ICONS.alertCircle} className="w-4 h-4" stroke="#f59e0b" width={1.8} />
          Click on an available slot to select.
        </p>
      </div>

      {/* Mobile only — same selected-slot summary + Add to Booking button as
          the desktop left card (see selectedSlotSummary/addToCartButton
          above), just repositioned below the slots grid so it's reachable
          right after picking a slot instead of scrolling back up. */}
      {selection && (
        <div className="lg:hidden bg-white rounded-2xl shadow-sm p-6 space-y-4">
          {selectedSlotSummary}
          {addToCartButton}
        </div>
      )}

      {cartPanel && <div className="lg:col-span-3">{cartPanel}</div>}
    </div>
  );
}

// ── Confirm Payment tab (look up a staff-created booking payment by its
// reference, then record a shroff/counter payment for it) ──────────────────
// Mirrors MembersManagement.jsx's ConfirmShroffPaymentTab. Lets staff resume
// the shroff confirm step later — from just the reference printed on a
// voucher — instead of only right after creating the booking on the "Book
// for Member" tab. Search is gated by "view_payments"; the confirm form
// itself only renders for "submit_manual_payment_receipts" — see
// BookingController::manualSearch/manualConfirm and searchBookingPayment()
// in bookingService.js.
function ConfirmBookingPaymentTab({ canConfirm }) {
  const [reference, setReference] = useState("");
  const [searching, setSearching] = useState(false);
  const [payment, setPayment] = useState(null);
  const [searchError, setSearchError] = useState("");

  const [paidAmount, setPaidAmount] = useState("");
  const [paidDate, setPaidDate] = useState("");
  const [receiptReference, setReceiptReference] = useState("");
  const [receiptFile, setReceiptFile] = useState(null);
  const [note, setNote] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState("");
  const [confirmFieldErrors, setConfirmFieldErrors] = useState({});
  const [confirmResult, setConfirmResult] = useState(null);
  const [downloadingVoucher, setDownloadingVoucher] = useState(false);

  const fieldError = (key) => {
    const msgs = confirmFieldErrors[key];
    return Array.isArray(msgs) && msgs[0] ? msgs[0] : null;
  };

  const resetConfirmForm = (p) => {
    // Left blank on purpose — the operator types the amount actually received.
    setPaidAmount("");
    setPaidDate(todayStr());
    setReceiptReference("");
    setReceiptFile(null);
    setNote("");
    setConfirmError("");
    setConfirmFieldErrors({});
    setConfirmResult(null);
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    const ref = reference.trim();
    if (!ref || searching) return;
    setSearching(true);
    setSearchError("");
    setPayment(null);
    setConfirmResult(null);
    try {
      const found = await searchBookingPayment(ref);
      setPayment(found);
      resetConfirmForm(found);
    } catch (err) {
      setSearchError(errInfo(err, "No booking payment found for that reference.").message);
    } finally {
      setSearching(false);
    }
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
    if (confirming || !payment || !receiptFile) return;
    setConfirmError("");
    setConfirmFieldErrors({});
    setConfirming(true);
    try {
      const result = await confirmBookingManualPayment({
        payment_reference: payment.payment_reference,
        paid_amount: paidAmount,
        paid_date: paidDate,
        receipt_reference: receiptReference,
        receipt: receiptFile,
        note: note || undefined,
      });
      toast.success(result.message || "Payment confirmed. Receipt pending director acknowledgement.");
      setConfirmResult(result);
    } catch (err) {
      const info = errInfo(err, "Could not confirm this payment.");
      setConfirmError(info.message);
      setConfirmFieldErrors(info.fields);
    } finally {
      setConfirming(false);
    }
  };

  const handleDownloadVoucher = async () => {
    if (!payment?.payment_id || downloadingVoucher) return;
    const tab = window.open("", "_blank");
    setDownloadingVoucher(true);
    try {
      const blob = await getBookingShroffVoucherPdf(payment.payment_id, payment.payment_reference);
      openBlobInTab(blob, tab);
    } catch (err) {
      tab?.close?.();
      toast.error(errInfo(err, "Could not load the shroff voucher.").message);
    } finally {
      setDownloadingVoucher(false);
    }
  };

  const isPending = payment?.payment_status === "Pending";

  return (
    <div className="space-y-4 max-w-2xl">
      <div className="bg-white rounded-2xl shadow-sm p-6">
        <h3 className="font-bold text-base mb-1" style={{ color: TOKENS.NAVY }}>
          Find a booking payment
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          Look up a staff-created booking by its payment reference (printed on the shroff voucher), then record
          the payment taken at the counter.
        </p>
        <form onSubmit={handleSearch} className="flex flex-wrap gap-3">
          <input
            className={`${inputClass} flex-1 min-w-[220px] font-mono`}
            placeholder="Payment reference, e.g. 07307000049"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
          />
          <button type="submit" disabled={searching || !reference.trim()} className={`${btnPrimary} !w-auto px-6`}>
            {searching ? "Searching…" : "Search"}
          </button>
        </form>
        {searchError && <p className="text-sm text-red-600 mt-3">{searchError}</p>}
      </div>

      {payment && (
        <div className="bg-white rounded-2xl shadow-sm p-6 space-y-5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-base font-bold truncate" style={{ color: TOKENS.NAVY }}>
                {[payment.payer?.title, payment.payer?.full_name].filter(Boolean).join(" ") || "—"}
              </p>
              <p className="text-xs text-gray-400 truncate">
                {[payment.member_code, payment.payer?.email].filter(Boolean).join(" — ")}
              </p>
            </div>
            <Pill>{payment.payment_status}</Pill>
          </div>

          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
            {[
              ["Payment reference", payment.payment_reference],
              ["Booking ID", payment.booking_id != null ? `#${payment.booking_id}` : null],
              ["Income", payment.income_description],
              ["Payable amount", money(payment.payable_amount)],
              [
                "Paid amount",
                payment.paid_amount != null && Number(payment.paid_amount) > 0 ? money(payment.paid_amount) : null,
              ],
              ["Paid at", payment.paid_at ? fmtDateTime(payment.paid_at) : null],
            ]
              .filter(([, v]) => v != null && v !== "")
              .map(([label, value]) => (
                <div key={label}>
                  <dt className="text-xs text-gray-400">{label}</dt>
                  <dd className="font-medium" style={{ color: TOKENS.NAVY }}>
                    {value}
                  </dd>
                </div>
              ))}
          </dl>

          {payment.payment_id && (
            <button
              type="button"
              onClick={handleDownloadVoucher}
              disabled={downloadingVoucher}
              className={`${btnSecondary} !w-auto px-4 inline-flex items-center gap-2`}
            >
              <Icon path={ICONS.download} className="w-4 h-4" width={1.8} />
              {downloadingVoucher ? "Opening…" : "Download Shroff Voucher"}
            </button>
          )}

          {!payment.manual_payment_allowed ? (
            <p className="text-sm text-gray-500 bg-gray-50 rounded-lg px-4 py-3">
              This is a member self-booking, paid online — shroff/manual payment isn't allowed for it.
            </p>
          ) : !isPending ? (
            <p className="text-sm text-gray-500 bg-gray-50 rounded-lg px-4 py-3">
              This payment is already <strong>{payment.payment_status}</strong> — nothing to confirm.
            </p>
          ) : payment.pending_manual_approval ? (
            <p className="text-sm text-gray-500 bg-gray-50 rounded-lg px-4 py-3">
              A manual payment approval request is already pending for this booking (Approval ID #
              {payment.pending_approval_id}) — awaiting Director/Admin acknowledgement.
            </p>
          ) : !canConfirm ? (
            <p className="text-sm text-gray-500 bg-gray-50 rounded-lg px-4 py-3">
              You don't have permission to confirm this payment.
            </p>
          ) : (
            <form onSubmit={handleConfirm} className="space-y-4 pt-4 border-t border-gray-100">
              <h4 className="text-sm font-bold" style={{ color: TOKENS.NAVY }}>
                Submit Shroff Receipt
              </h4>
              <p className="text-xs text-gray-400 -mt-2">
                Submitting confirms this payment immediately. A Director/Admin will acknowledge the receipt
                afterward for audit purposes.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>
                    Paid amount <span className="text-gray-400 font-normal">(must match payable amount)</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className={inputClass}
                    value={paidAmount}
                    onChange={(e) => setPaidAmount(e.target.value)}
                    required
                  />
                  {fieldError("paid_amount") && <p className="text-xs text-red-600 mt-1">{fieldError("paid_amount")}</p>}
                </div>
                <div>
                  <label className={labelClass}>Paid date</label>
                  <input
                    type="date"
                    className={inputClass}
                    value={paidDate}
                    onChange={(e) => setPaidDate(e.target.value)}
                    required
                  />
                  {fieldError("paid_date") && <p className="text-xs text-red-600 mt-1">{fieldError("paid_date")}</p>}
                </div>
              </div>
              <div>
                <label className={labelClass}>Receipt / slip reference</label>
                <input
                  className={inputClass}
                  value={receiptReference}
                  onChange={(e) => setReceiptReference(e.target.value)}
                  required
                />
                {fieldError("receipt_reference") && (
                  <p className="text-xs text-red-600 mt-1">{fieldError("receipt_reference")}</p>
                )}
              </div>
              <ReceiptFileInput file={receiptFile} onChange={setReceiptFile} error={fieldError("receipt")} />
              <div>
                <label className={labelClass}>
                  Note <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <textarea className={`${inputClass} resize-none`} rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
              </div>

              {confirmError && <p className="text-sm text-red-600">{confirmError}</p>}

              <button type="submit" disabled={confirming || !receiptFile} className={`${btnPrimary} w-full`}>
                {confirming ? "Confirming…" : "Confirm Payment"}
              </button>
            </form>
          )}

          {confirmResult && (
            <div className="rounded-lg bg-emerald-50 border border-emerald-100 px-4 py-3 text-sm text-emerald-800">
              {confirmResult.message || "Payment confirmed. Receipt pending director acknowledgement."}
              {confirmResult.approval_id && (
                <>
                  {" "}
                  Approval ID: <span className="font-mono font-semibold">#{confirmResult.approval_id}</span> — will
                  be acknowledged on the Payments page's Manual Approvals tab.
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────
export default function BookingsManagement() {
  const { can } = useAuth();
  const canBookForMember = can("create_bookings");
  const canViewPayments = can("view_payments");
  const canConfirmPayments = can("submit_manual_payment_receipts");
  // The "Confirm Payment" tab is also shown to holders of
  // "confirm_shroff_payments" (e.g. the Shroff role) — the backend's
  // */manual-confirm routes accept either permission.
  const canConfirmShroff = can("confirm_shroff_payments");
  const canSeeConfirmTab = canViewPayments || canConfirmShroff;
  const canSubmitConfirm = canConfirmPayments || canConfirmShroff;

  const tabs = [
    { key: "bookings", label: "Bookings" },
    // Tab itself stays visible/reachable even while FEATURE_FLAGS.facilityBooking
    // is off — it just renders a "temporarily disabled" notice instead of
    // StaffBookingTab below, same as the member/coach dashboards' booking tabs.
    canBookForMember && { key: "book", label: "Book for Member" },
    canSeeConfirmTab && { key: "confirm", label: "Confirm Payment" },
  ].filter(Boolean);

  const [activeTab, setActiveTab] = useState("bookings");

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="text-xl font-bold" style={{ color: TOKENS.NAVY }}>
            Bookings
          </h1>
          <p className="text-sm text-gray-500">Facility bookings made by members, and staff bookings on their behalf.</p>
        </div>
      </div>

      {tabs.length > 1 && (
        <div className="flex gap-1 mb-5 border-b border-gray-200 overflow-x-auto">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`px-4 py-2.5 text-sm font-semibold border-b-2 whitespace-nowrap flex-shrink-0 transition ${
                activeTab === t.key ? "border-current" : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
              style={activeTab === t.key ? { color: TOKENS.PRIMARY } : undefined}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {activeTab === "bookings" && <BookingsTab />}
      {activeTab === "book" && canBookForMember && (
        FEATURE_FLAGS.facilityBooking ? (
          <StaffBookingTab />
        ) : (
          <div className="bg-white rounded-2xl shadow-sm p-8 text-center max-w-lg mx-auto">
            <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: "#fef3c7" }}>
              <Icon path={ICONS.block} className="w-6 h-6" stroke="#b45309" width={1.8} />
            </div>
            <h3 className="font-bold text-base mb-1" style={{ color: TOKENS.NAVY }}>Facility Booking Temporarily Unavailable</h3>
            <p className="text-sm text-gray-500">
              New bookings are temporarily disabled. Existing bookings are still visible on the Bookings tab.
            </p>
          </div>
        )
      )}
      {activeTab === "confirm" && canSeeConfirmTab && <ConfirmBookingPaymentTab canConfirm={canSubmitConfirm} />}
    </div>
  );
}
